import axios from "axios";
import {
    clearAuthSession,
    getAccessToken,
    getRefreshToken,
    setAuthSession,
} from "../utils/authStorage";
import { getCurrentUserId } from "../utils/reduxUser";

const LOGIN_PATH = "/auth/login";

const isAuthPath = (url = "") => {
    if (!url) return false;
    return String(url).includes("/auth/");
};

const isObjectPayload = (data) => {
    return Boolean(data) &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        !(data instanceof FormData);
};

const redirectToLogin = () => {
    if (window.location.pathname !== LOGIN_PATH) {
        window.location.replace(LOGIN_PATH);
    }
};

class APIClient {
    constructor(baseURL) {
        this.baseURL = baseURL;
        this.isRefreshing = false;
        this.pendingRequests = [];

        this.client = axios.create({
            baseURL,
        })

        this.client.interceptors.request.use((config) => {
            const accessToken = getAccessToken();
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }

            const userId = getCurrentUserId();

            if (
                userId &&
                !config.skipEmployeeIdInjection &&
                isObjectPayload(config.data) &&
                (config.method === "post" || config.method === "patch") &&
                config.data.emp_id === undefined
            ) {
                config.data.emp_id = userId;
            }

            return config;
        });

        this.client.interceptors.response.use(
            (response) => response,
            async (error) => {
                const responseCode = error?.response?.data?.code;
                const originalRequest = error?.config;

                if (!originalRequest) {
                    return Promise.reject(error);
                }

                if ((responseCode === "TOKEN_MISSING" || responseCode === "TOKEN_INVALID") && !isAuthPath(originalRequest.url)) {
                    clearAuthSession();
                    redirectToLogin();
                    return Promise.reject(error);
                }

                if (responseCode !== "TOKEN_EXPIRED" || isAuthPath(originalRequest.url) || originalRequest.__retry) {
                    return Promise.reject(error);
                }

                originalRequest.__retry = true;

                if (this.isRefreshing) {
                    return new Promise((resolve, reject) => {
                        this.pendingRequests.push({ resolve, reject });
                    })
                        .then((token) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return this.client(originalRequest);
                        })
                        .catch((refreshError) => Promise.reject(refreshError));
                }

                this.isRefreshing = true;
                const refreshToken = getRefreshToken();

                if (!refreshToken) {
                    clearAuthSession();
                    redirectToLogin();
                    this.pendingRequests.forEach(({ reject }) => reject(error));
                    this.pendingRequests = [];
                    this.isRefreshing = false;
                    return Promise.reject(error);
                }

                try {
                    const refreshUrl = this.baseURL
                        ? `${String(this.baseURL).replace(/\/$/, "")}/auth/refresh`
                        : "/auth/refresh";

                    const refreshResponse = await axios.post(refreshUrl, {
                        refresh_token: refreshToken,
                    });

                    setAuthSession({
                        accessToken: refreshResponse.data.access_token,
                        refreshToken: refreshResponse.data.refresh_token,
                    });

                    const newAccessToken = refreshResponse.data.access_token;
                    this.pendingRequests.forEach(({ resolve }) => resolve(newAccessToken));
                    this.pendingRequests = [];

                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return this.client(originalRequest);
                } catch (refreshError) {
                    const refreshErrorCode = refreshError?.response?.data?.code;

                    clearAuthSession();
                    this.pendingRequests.forEach(({ reject }) => reject(refreshError));
                    this.pendingRequests = [];

                    if (refreshErrorCode === "REFRESH_EXPIRED" || refreshErrorCode === "USER_NOT_FOUND" || !isAuthPath(window.location.pathname)) {
                        redirectToLogin();
                    }

                    return Promise.reject(refreshError);
                } finally {
                    this.isRefreshing = false;
                }
            }
        );
    }

    async get(path, config = {}) {
        return await this.client.get(path, config);
    }

    async post(path, data = {}, config = {}) {
        return await this.client.post(path, data, config);
    }

    async delete(path, config = {}) {
        return await this.client.delete(path, config);
    }

    async patch(path, data = {}, config = {}) {
        return await this.client.patch(path, data, config);
    }

    async put(path, data = {}, config = {}) {
        return await this.client.put(path, data, config);
    }
}

const filterData = (data) => {
    return Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== null)
    );
}

const API = new APIClient(process.env.REACT_APP_API_URL,);

export default APIClient;
export { API, filterData };
